import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTournamentDetails } from '../../store/slices/tournamentSlice';
import { createEnrollmentPreference, fetchMyTeams, clearPreferenceUrl } from '../../store/slices/teamSlice';
import { fetchMatches, fetchStandings } from '../../store/slices/matchSlice';
import {
    Calendar, MapPin, Users, Trophy, ChevronLeft, Flag, CheckCircle2, ShieldAlert, Plus, Ticket, X
} from 'lucide-react';

const TournamentDetails = () => {
    const { slug, id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { tournamentDetails, loading: loadingTourn, error: errorTourn } = useSelector(state => state.tournaments);
    const { myTeams, loading: loadingTeam, preferenceUrl } = useSelector(state => state.teams);
    const { isAuthenticated, user } = useSelector(state => state.auth);
    const { matches, standings } = useSelector(state => state.matches);

    const [teamName, setTeamName] = useState('');
    const [showCheckout, setShowCheckout] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [loadingTicket, setLoadingTicket] = useState(false);

    useEffect(() => {
        dispatch(fetchTournamentDetails({ slug, id }));
        dispatch(fetchMatches(id));
        dispatch(fetchStandings(id));
        if (isAuthenticated) {
            dispatch(fetchMyTeams(id));
        }
    }, [slug, id, dispatch, isAuthenticated]);

    useEffect(() => {
        if (preferenceUrl) {
            window.location.href = preferenceUrl;
        }
    }, [preferenceUrl]);

    const handleEnrollClick = () => {
        if (!isAuthenticated) {
            alert('Debes iniciar sesión para inscribir a un equipo.');
            return;
        }
        setShowCheckout(true);
    };

    
    const handleBuyMatchTicket = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            alert('Debes iniciar sesión para comprar entradas.');
            return;
        }
        if (!selectedMatch) return;
        
        setLoadingTicket(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/mercadopago/crear-preferencia-partido', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ matchId: selectedMatch._id, quantity: ticketQuantity, origin: 'web' })
            });
            const data = await res.json();
            if (data.init_point) {
                window.location.href = data.init_point;
            } else {
                alert(data.error || 'Error al procesar el pago');
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión');
        } finally {
            setLoadingTicket(false);
        }
    };

    const submitEnrollment = (e) => {
        e.preventDefault();
        if (!teamName.trim()) return;
        dispatch(createEnrollmentPreference({ tournamentId: id, teamName }));
    };

    if (loadingTourn && !tournamentDetails) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (errorTourn || !tournamentDetails) {
        return (
            <div className="min-h-screen bg-[#080808] flex items-center justify-center text-white">
                <p>Torneo no encontrado.</p>
            </div>
        );
    }

    const isFull = tournamentDetails.enrolledTeams >= tournamentDetails.maxTeams;

    return (
        <div className="min-h-screen bg-[#080808] text-white pb-24">
            {/* Portada */}
            <div className="relative h-[40vh] md:h-[50vh] w-full bg-zinc-900 border-b border-white/10 overflow-hidden">
                {tournamentDetails.coverUrl ? (
                    <img
                        src={tournamentDetails.coverUrl}
                        alt={tournamentDetails.name}
                        className="w-full h-full object-cover opacity-60"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-20">
                        <Trophy size={80} />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808] to-transparent" />

                <button
                    onClick={() => navigate(`/${slug}`)}
                    className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-md rounded-full text-white/80 hover:text-white border border-white/10 transition-all z-10"
                >
                    <ChevronLeft size={18} />
                    <span className="text-xs font-black uppercase tracking-[2px]">Volver</span>
                </button>
            </div>

            {/* Contenido */}
            <div className="max-w-4xl mx-auto px-6 -mt-20 relative z-10">
                <div className="bg-glass rounded-[40px] p-8 md:p-12 shadow-3xl border border-white/5 mb-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-blue-500/20">
                            {tournamentDetails.sport}
                        </span>
                        <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border ${tournamentDetails.status === 'En Curso' ? 'bg-green-500/20 text-green-400 border-green-500/20' :
                            tournamentDetails.status === 'Finalizado' ? 'bg-zinc-800 text-zinc-400 border-zinc-700' :
                                'bg-blue-500/20 text-blue-400 border-blue-500/20'
                            }`}>
                            {tournamentDetails.status}
                        </span>
                        {isFull && <span className="px-3 py-1 bg-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest rounded-full border border-red-500/20">Agotado</span>}
                    </div>

                    <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">{tournamentDetails.name}</h1>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-y border-white/5 py-8">
                        <div className="space-y-1">
                            <p className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Sede</p>
                            <p className="font-bold text-white flex items-center gap-2"><MapPin size={14} className="text-blue-500" /> {tournamentDetails.location || '-'}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Fechas</p>
                            <p className="font-bold text-white flex items-center gap-2">
                                <Calendar size={14} className="text-blue-500" />
                                {tournamentDetails.startDate ? new Date(tournamentDetails.startDate).toLocaleDateString() : '-'}
                            </p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Equipos</p>
                            <p className="font-bold text-white flex items-center gap-2"><Users size={14} className="text-blue-500" /> {tournamentDetails.enrolledTeams}/{tournamentDetails.maxTeams}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-black text-white/30 uppercase tracking-[2px]">Inscripción</p>
                            <p className="font-bold text-white flex items-center gap-2 text-xl tracking-tight text-green-400 opacity-90">${tournamentDetails.registrationFee}</p>
                        </div>
                    </div>

                    {tournamentDetails.description && (
                        <div className="mb-10">
                            <h3 className="text-lg font-black mb-4 uppercase tracking-widest text-white/40">Detalles y Formato</h3>
                            <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{tournamentDetails.description}</p>
                        </div>
                    )}

                    {tournamentDetails.prize && (
                        <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/5 border border-yellow-500/20 rounded-3xl p-6 mb-10 flex items-center gap-4">
                            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center shrink-0">
                                <Trophy size={24} className="text-yellow-500" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-yellow-500/60 uppercase tracking-[2px] mb-1">Premio al Campeón</p>
                                <p className="font-black text-yellow-500 text-lg">{tournamentDetails.prize}</p>
                            </div>
                        </div>
                    )}

                    {/* MIS EQUIPOS & CHECKOUT */}
                    <div className="mt-8 pt-8 border-t border-white/5">

                        {/* 1. Lista de Mis Equipos */}
                        {myTeams && myTeams.length > 0 && (
                            <div className="mb-8">
                                <h3 className="text-xl font-black mb-4 uppercase tracking-tight flex items-center gap-2">
                                    <ShieldAlert className="text-blue-500" size={24} /> Mis Equipos Inscritos
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {myTeams.map(team => (
                                        <div key={team._id} className="bg-blue-500/10 border border-blue-500/20 rounded-[24px] p-6 flex flex-col justify-between">
                                            <div>
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="text-xl font-black truncate pr-2" title={team.name}>{team.name}</h4>
                                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${team.paymentStatus === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                                        {team.paymentStatus === 'approved' ? 'Confirmado' : 'Pendiente'}
                                                    </span>
                                                </div>
                                                <p className="text-white/50 text-sm mb-6 flex items-center gap-1"><Users size={14} /> {team.players?.length || 0} Jugadores</p>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/${slug}/tournaments/${id}/my-team/${team._id}`)}
                                                className="w-full bg-blue-600/80 hover:bg-blue-500 py-3 rounded-xl font-bold uppercase tracking-widest transition-colors text-sm"
                                            >
                                                Gestionar
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 2. Formulario de Inscripción o Call To Action */}
                        {showCheckout ? (
                            <form onSubmit={submitEnrollment} className="bg-white/5 border border-white/10 rounded-[32px] p-6 md:p-8 animate-in slide-in-from-bottom-4 duration-300">
                                <h3 className="text-xl font-black mb-2 uppercase tracking-tight">Inscribir Nuevo Equipo</h3>
                                <p className="text-sm text-white/40 mb-6">Elegí el nombre que los va a representar. Luego serás redirigido a Mercado Pago para abonar la inscripción de <strong>${tournamentDetails.registrationFee}</strong>.</p>

                                <div className="space-y-2 mb-6">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Nombre del Equipo</label>
                                    <input
                                        type="text"
                                        required
                                        value={teamName}
                                        onChange={e => setTeamName(e.target.value)}
                                        placeholder="Ej: Los Galácticos"
                                        className="w-full bg-[#080808] border border-white/10 rounded-2xl p-5 text-lg font-black focus:border-blue-500/50 outline-none transition-all"
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <button type="button" onClick={() => setShowCheckout(false)} className="px-6 py-4 font-bold text-white/40 hover:text-white transition-colors">Cancelar</button>
                                    <button
                                        type="submit"
                                        disabled={loadingTeam || !teamName.trim()}
                                        className="flex-1 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-black py-4 rounded-xl shadow-lg shadow-blue-500/20 hover:scale-[1.02] flex justify-center items-center gap-2 disabled:opacity-50 disabled:scale-100"
                                    >
                                        {loadingTeam ? <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : 'IR A PAGAR'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="flex flex-col md:flex-row items-center gap-6 justify-between bg-black/40 p-6 rounded-[32px] border border-white/5">
                                <div>
                                    <p className="text-xl font-black mb-1">
                                        {(myTeams && myTeams.length > 0) ? '¿Tenés otro equipo?' : '¿Todo listo para jugar?'}
                                    </p>
                                    <p className="text-white/40 text-sm font-medium">
                                        {(myTeams && myTeams.length > 0) ? 'Podés inscribir a más equipos y gestionarlos por separado.' : 'Asegurá el lugar de tu escuadra ahora mismo.'}
                                    </p>
                                </div>
                                <button
                                    onClick={handleEnrollClick}
                                    disabled={isFull || tournamentDetails.status !== 'Inscripciones Abiertas'}
                                    className="w-full md:w-auto px-8 py-5 flex items-center justify-center gap-2 bg-white text-black font-black rounded-[20px] hover:scale-105 active:scale-95 transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {(myTeams && myTeams.length > 0) ? <><Plus size={20} /> INSCRIBIR OTRO EQUIPO</> : (isFull ? 'CUPO LLENO' : tournamentDetails.status !== 'Inscripciones Abiertas' ? 'INSCRIPCIONES CERRADAS' : 'INSCRIBIR EQUIPO')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* TABLA DE POSICIONES Y FIXTURE */}
                    {tournamentDetails.status !== 'Inscripciones Abiertas' && (
                        <div className="mt-16 pt-12 border-t border-white/5">

                            {/* Posiciones */}
                            {standings?.length > 0 && (
                                <div className="mb-16">
                                    <h3 className="text-3xl font-black mb-8 uppercase tracking-tight flex items-center gap-4">
                                        <Trophy className="text-blue-500" size={28} /> Tabla de Posiciones
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse min-w-[600px]">
                                            <thead>
                                                <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase tracking-[2px]">
                                                    <th className="pb-4 pl-4 font-black w-12">#</th>
                                                    <th className="pb-4 font-black">Equipo</th>
                                                    <th className="pb-4 text-center font-black" title="Partidos Jugados">PJ</th>
                                                    <th className="pb-4 text-center font-black" title="Ganados">G</th>
                                                    <th className="pb-4 text-center font-black" title="Empatados">E</th>
                                                    <th className="pb-4 text-center font-black" title="Perdidos">P</th>
                                                    <th className="pb-4 text-center font-black" title="Diferencia de Gol">DG</th>
                                                    <th className="pb-4 pr-4 text-center font-black text-white" title="Puntos">PTS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {standings.map((st, i) => (
                                                    <tr key={st.team._id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors group">
                                                        <td className="py-4 pl-4 font-black text-white/40 group-hover:text-white transition-colors">{i + 1}</td>
                                                        <td className="py-4 font-black">{st.team.name}</td>
                                                        <td className="py-4 text-center text-white/60">{st.played}</td>
                                                        <td className="py-4 text-center text-green-400/80">{st.won}</td>
                                                        <td className="py-4 text-center text-white/40">{st.drawn}</td>
                                                        <td className="py-4 text-center text-red-400/80">{st.lost}</td>
                                                        <td className="py-4 text-center font-medium text-white/60">{st.goalDifference > 0 ? `+${st.goalDifference}` : st.goalDifference}</td>
                                                        <td className="py-4 pr-4 text-center font-black text-lg text-blue-400">{st.points}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Fixture / Resultados */}
                            {matches?.length > 0 && (
                                <div>
                                    <h3 className="text-3xl font-black mb-8 uppercase tracking-tight flex items-center gap-4">
                                        <Calendar className="text-blue-500" size={28} /> Fixture y Resultados
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {matches.map(match => (
                                            <div key={match._id} className="bg-white/5 border border-white/10 rounded-[24px] p-5 relative overflow-hidden group hover:border-blue-500/30 transition-all">
                                                <div className={`absolute top-0 left-0 w-full h-1 ${match.status === 'Finalizado' ? 'bg-green-500' :
                                                    match.status === 'En Juego' ? 'bg-yellow-500' :
                                                        'bg-blue-500'
                                                    }`} />

                                                <div className="flex justify-between items-center mb-4">
                                                    <span className="text-[10px] font-black uppercase text-white/40 tracking-[2px]">{match.roundName}</span>
                                                    <div className="flex items-center gap-2">
                                                        {match.isTicketed && match.status !== 'Finalizado' && (
                                                            <span className="text-[10px] font-black uppercase tracking-[2px] px-2 py-1 rounded bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.2)]">
                                                                ENTRADAS ${match.ticketPrice}
                                                            </span>
                                                        )}
                                                        <span className={`text-[10px] font-black uppercase tracking-[2px] px-2 py-1 rounded bg-white/5 ${match.status === 'Finalizado' ? 'text-green-400' :
                                                            match.status === 'En Juego' ? 'text-yellow-400 animate-pulse' :
                                                                'text-blue-400'
                                                            }`}>
                                                            {match.status}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center bg-black/40 rounded-xl p-4 border border-white/5">
                                                    <p className="font-black text-sm w-[40%] truncate text-right" title={match.teamA?.name || 'A Definir'}>{match.teamA?.name || 'A Definir'}</p>
                                                    <div className="w-[20%] text-center">
                                                        {match.status === 'Programado' ? (
                                                            <span className="text-white/40 font-bold text-xs uppercase">vs</span>
                                                        ) : (
                                                            <span className="text-2xl font-black tracking-tighter">{match.scoreA} - {match.scoreB}</span>
                                                        )}
                                                    </div>
                                                    <p className="font-black text-sm w-[40%] truncate" title={match.teamB?.name || 'A Definir'}>{match.teamB?.name || 'A Definir'}</p>
                                                </div>

                                                {(match.date || match.location) && (
                                                    <div className="mt-4 flex flex-wrap gap-4 text-xs font-medium text-white/40 justify-center">
                                                        {match.date && (
                                                            <span className="flex items-center gap-1"><Calendar size={12} />{new Date(match.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                        )}
                                                        {match.location && (
                                                            <span className="flex items-center gap-1"><MapPin size={12} />{match.location}</span>
                                                        )}
                                                    </div>
                                                )}
                                                {match.isTicketed && match.status !== 'Finalizado' && (
                                                    <div className="mt-4 pt-4 border-t border-white/5 flex justify-center">
                                                        <button 
                                                            onClick={() => setSelectedMatch(match)}
                                                            className="w-full py-3 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 rounded-xl font-black text-xs transition-colors flex items-center justify-center gap-2 uppercase tracking-widest shadow-[0_0_15px_rgba(59,130,246,0.15)]"
                                                        >
                                                            <Ticket size={16} /> Comprar Entrada
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* Modal Comprar Entrada Partido */}
            {selectedMatch && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#0f0f0f] border border-white/10 rounded-[32px] w-full max-w-md p-8 animate-in zoom-in-95 duration-200 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-blue-600/10 blur-3xl rounded-full mix-blend-screen pointer-events-none" />
                        
                        <div className="flex justify-between items-start mb-6 relative z-10">
                            <div>
                                <h2 className="text-2xl font-black mb-1">Comprar Entradas</h2>
                                <p className="text-white/40 text-sm">{selectedMatch.teamA?.name || 'A Definir'} vs {selectedMatch.teamB?.name || 'A Definir'}</p>
                            </div>
                            <button onClick={() => setSelectedMatch(null)} className="p-2 bg-white/5 hover:bg-white/10 rounded-full text-white/40 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleBuyMatchTicket} className="relative z-10">
                            <div className="bg-black/40 border border-white/5 rounded-2xl p-5 mb-6">
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-white/60 font-medium">Precio por entrada</span>
                                    <span className="text-xl font-black text-white">${selectedMatch.ticketPrice}</span>
                                </div>
                                <div className="flex justify-between items-center border-t border-white/5 pt-4">
                                    <span className="text-white/60 font-medium">Cantidad</span>
                                    <div className="flex items-center gap-4 bg-white/5 rounded-xl border border-white/10 p-1">
                                        <button 
                                            type="button" 
                                            onClick={() => setTicketQuantity(Math.max(1, ticketQuantity - 1))}
                                            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-black"
                                        >-</button>
                                        <span className="font-black w-4 text-center">{ticketQuantity}</span>
                                        <button 
                                            type="button" 
                                            onClick={() => setTicketQuantity(Math.min(10, ticketQuantity + 1))}
                                            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-black"
                                        >+</button>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-between items-center mb-8 px-2">
                                <span className="text-white/40 text-sm font-medium">Total a pagar (inc. servicio)</span>
                                <span className="text-3xl font-black text-blue-400">${(selectedMatch.ticketPrice * 1.15 * ticketQuantity).toFixed(2)}</span>
                            </div>

                            <button 
                                type="submit" 
                                disabled={loadingTicket}
                                className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black tracking-widest shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transition-all flex justify-center items-center gap-2 disabled:opacity-50 disabled:scale-100 uppercase"
                            >
                                {loadingTicket ? <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : 'IR A PAGAR'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TournamentDetails;
